import React, { useEffect, useRef, useState } from 'react';
import { Building2, Loader2, Plus, RefreshCw } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import PartnerAccountDialog from '../components/external-partners/PartnerAccountDialog';
import PartnerUsageTooltip from '../components/external-partners/PartnerUsageTooltip';
import { listExtPartnerUsers } from '../services/extPartnerAdmin';

const PAGE_SIZE = 25;
const lastLoginLabel = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export default function ExtPartnerUsersPage() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState(null);
  const createButton = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    listExtPartnerUsers({
      query,
      page,
      limit: PAGE_SIZE,
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted) setResult(data);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [query, page, refresh]);

  const saved = (message) => {
    setDialog(null);
    setNotice(message);
    setRefresh((value) => value + 1);
  };
  const totalPages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));

  return (
    <MainLayout activeTab="externalPartners">
      <div className="flex flex-col h-full min-h-0 bg-gray-50">
        <div className="px-6 py-5 bg-white border-b flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Building2 className="text-blue-600" />
              External Partners
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage partner logins and passwords.
            </p>
          </div>
          <Button
            ref={createButton}
            onClick={(event) => {
              setNotice('');
              setDialog({ user: null, trigger: event.currentTarget });
            }}
          >
            <Plus />
            Create partner
          </Button>
        </div>
        <div className="px-6 py-4 flex gap-3 items-end border-b bg-white">
          <div className="w-full max-w-lg">
            <label
              htmlFor="partner-search"
              className="block text-sm font-medium mb-1"
            >
              Search users
            </label>
            <Input
              id="partner-search"
              value={search}
              placeholder="Company, user name or login email"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => setRefresh((value) => value + 1)}
          >
            <RefreshCw />
            Refresh
          </Button>
        </div>
        {notice && (
          <div
            role="status"
            className="mx-6 mt-4 p-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-md"
          >
            {notice}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto p-6">
          {loading ? (
            <div
              role="status"
              className="flex justify-center items-center gap-2 py-16 text-gray-600"
            >
              <Loader2 className="animate-spin" />
              Loading partner users…
            </div>
          ) : error ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700"
            >
              <p>{error}</p>
              <Button
                className="mt-3"
                variant="outline"
                onClick={() => setRefresh((value) => value + 1)}
              >
                Retry
              </Button>
            </div>
          ) : result.users.length === 0 ? (
            <div className="text-center py-16 rounded-lg border bg-white">
              <Building2 className="mx-auto mb-3 text-gray-400" />
              <h2 className="font-medium">No partner users found</h2>
              <p className="text-sm text-gray-500 mt-1">
                {query
                  ? 'Try a different company, name or email.'
                  : 'Create a partner to add its first login.'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border bg-white overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {[
                      'Partner',
                      'User name',
                      'Login email',
                      'Role',
                      'Status',
                      'Last login',
                      'Actions',
                    ].map((label) => (
                      <th
                        key={label}
                        scope="col"
                        className="px-4 py-3 font-medium whitespace-nowrap"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.users.map((user) => (
                    <tr key={user.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1">
                          {user.partner?.name || '—'}
                          <PartnerUsageTooltip partner={user.partner} />
                        </span>
                      </td>
                      <td className="px-4 py-3">{user.name || '—'}</td>
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">{user.role}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${user.status === 'ACTIVE' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {lastLoginLabel(user.lastLoginAt)}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Update password for ${user.email}`}
                          onClick={(event) => {
                            setNotice('');
                            setDialog({ user, trigger: event.currentTarget });
                          }}
                        >
                          Update password
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && !error && (
          <div className="px-6 py-3 bg-white border-t flex justify-between items-center text-sm text-gray-600">
            <span>
              {result.total} user{result.total === 1 ? '' : 's'} · Page {page}{' '}
              of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        {dialog && (
          <PartnerAccountDialog
            user={dialog.user}
            onClose={() => setDialog(null)}
            onSaved={saved}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const target = dialog.trigger?.isConnected
                ? dialog.trigger
                : createButton.current;
              target?.focus();
            }}
          />
        )}
      </div>
    </MainLayout>
  );
}
